import { __decorate } from "tslib";
import { attr } from '@microsoft/fast-element';
import { LoadingButton } from '../common/LoadingButton';
import { DEV } from 'esm-env';
export class ButtonComponent extends LoadingButton {
    constructor() {
        super(...arguments);
        this.baseName = 'button';
        this.size = 'medium';
        this.variant = 'primary';
        this.fullwidth = false;
        this.disabled = false;
        this.type = 'button';
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAccessibleLabel();
        // This line is necessary to make it compatible with Button before the audit
        if (this.variant === 'tertiary-neutral') {
            this.variant = 'tertiary';
            if (DEV) {
                console.warn('Chameleon Button: The variant `tertiary-neutral` is deprecated. Use `tertiary` instead');
            }
        }
        if (this.variant === 'neutral-inverse') {
            this.variant = 'inverted';
            if (DEV) {
                console.warn('Chameleon Button: The variant `neutral-inverse` is deprecated. Use `inverted` instead');
            }
        }
    }
    getInternalButtonElement() {
        return this.shadowRoot?.querySelector('button');
    }
    disabledChanged(oldValue, newValue) {
        super.disabledChanged(oldValue, newValue);
        requestAnimationFrame(() => {
            this.getInternalButtonElement()?.removeAttribute('disabled');
            this.getInternalButtonElement()?.setAttribute('aria-disabled', `${newValue}`);
        });
    }
    labelChanged() {
        this.setAccessibleLabel();
    }
    labelIdChanged() {
        this.setAccessibleLabel();
    }
    // We need to overwrite the internal keypress handler because FAST uses this to add the "submit on Enter press when inside a form"
    // behaviour to custom input fields. However in the case of the `button`, since we render actual HTML button elements inside our web-components
    // this only causes buggy behaviour like `type="button"` submitting the form on `Enter` or a `type="submit"` submitting the form twice.
    // source: https://github.com/microsoft/fast/blob/f05058084183576b641d33b18e0cdd205814ffdf/packages/web-components/fast-foundation/src/form-associated/form-associated.ts#L660
    _keypressHandler(event) {
        if (this.getInternalButtonElement()?.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
        }
        // We don't do anything here because buttons automatically handle form submissions on keypress
    }
    getInternalTextContent() {
        // Parse text content to remove the svg code portion of the string for leading and/or trailing icons, if they exist. Replace is run twice, in case both are present.
        // Remaining string represents visible text content provided to the button
        const regex = /<svg(.*?)<\\\/svg>/;
        return this.textContent?.replace(regex, '')?.replace(regex, '');
    }
    setAccessibleLabel() {
        if (this.getInternalTextContent()) {
            return;
        }
        if (this.labelId) {
            this.setAttribute('aria-labelledby', this.labelId);
            return;
        }
        if (this.label) {
            this.getInternalButtonElement()?.setAttribute('aria-label', this.label);
            return;
        }
        if (DEV) {
            this.validateAccessibleLabel();
        }
    }
    validateAccessibleLabel() {
        if (!this.getInternalButtonElement()?.getAttribute('aria-label')?.length &&
            !this.getAttribute('aria-labelledby')?.length) {
            console.error(`CHAMELEON-A11Y: Button component must have an accessible label provided by either 
  1) text content 
  2) a visible label whose id is given as the attribute labelId 
  3) a non-empty label attribute to be used as aria-label.
`);
        }
    }
}
__decorate([
    attr
], ButtonComponent.prototype, "size", void 0);
__decorate([
    attr
], ButtonComponent.prototype, "variant", void 0);
__decorate([
    attr({ mode: 'boolean' })
], ButtonComponent.prototype, "fullwidth", void 0);
__decorate([
    attr({ mode: 'boolean' })
], ButtonComponent.prototype, "disabled", void 0);
__decorate([
    attr
], ButtonComponent.prototype, "type", void 0);
__decorate([
    attr({ attribute: 'label-id' })
], ButtonComponent.prototype, "labelId", void 0);
__decorate([
    attr
], ButtonComponent.prototype, "label", void 0);
